import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import {
  Receipt,
  Plus,
  Search,
  Calendar,
  DollarSign,
  Check,
  Clock,
  AlertCircle,
  Building,
  Zap,
  Droplet,
  Wifi,
  Phone,
  CreditCard,
  Home,
  Car,
  GraduationCap,
  Heart,
  ShoppingBag,
  MoreHorizontal,
  ArrowRight,
  Trash2,
  Edit,
  Bell,
  RefreshCw,
  Loader2,
  X
} from 'lucide-react'
import { BillsAPI, AccountsAPI } from '../services/api'

// Category icon mapping
const getCategoryIcon = (category) => {
  const icons = {
    utilities: Zap,
    electric: Zap,
    water: Droplet,
    internet: Wifi,
    phone: Phone,
    'credit card': CreditCard,
    'credit-card': CreditCard,
    loan: Home,
    mortgage: Home,
    insurance: Heart,
    subscription: ShoppingBag,
    default: Building
  }
  return icons[category?.toLowerCase()] || icons.default
}

const getCategoryColor = (category) => {
  const colors = {
    utilities: 'bg-yellow-50 text-yellow-600',
    electric: 'bg-yellow-50 text-yellow-600',
    water: 'bg-blue-50 text-blue-600',
    internet: 'bg-purple-50 text-purple-600',
    phone: 'bg-emerald-50 text-emerald-600',
    'credit card': 'bg-slate-50 text-slate-600',
    'credit-card': 'bg-slate-50 text-slate-600',
    loan: 'bg-orange-50 text-orange-600',
    mortgage: 'bg-orange-50 text-orange-600',
    insurance: 'bg-red-50 text-red-600',
    subscription: 'bg-pink-50 text-pink-600',
    default: 'bg-slate-50 text-slate-600'
  }
  return colors[category?.toLowerCase()] || colors.default
}

export default function PayBillsPage() {
  const { t, i18n } = useTranslation(['bills', 'common'])
  const [activeTab, setActiveTab] = useState('payees')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddPayee, setShowAddPayee] = useState(false)
  const [selectedPayee, setSelectedPayee] = useState(null)
  const [showPayModal, setShowPayModal] = useState(false)

  // Data states
  const [payees, setPayees] = useState([])
  const [payments, setPayments] = useState([])
  const [upcomingBills, setUpcomingBills] = useState([])
  const [accounts, setAccounts] = useState([])
  const [categories, setCategories] = useState([])

  // UI states
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')

  // Form states
  const [newPayee, setNewPayee] = useState({
    name: '',
    category: '',
    account_number: '',
    routing_number: '',
    autopay: false
  })
  const [paymentData, setPaymentData] = useState({
    accountId: '',
    amount: '',
    scheduledDate: '',
    memo: ''
  })

  // Summary stats
  const [totalPaid, setTotalPaid] = useState(0)
  const [autopayCount, setAutopayCount] = useState(0)

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [payeesRes, paymentsRes, upcomingRes, accountsRes] = await Promise.all([
        BillsAPI.getPayees(),
        BillsAPI.getPayments({ limit: 20 }),
        BillsAPI.getUpcoming(),
        AccountsAPI.getAll()
      ])

      if (payeesRes.success) {
        setPayees(payeesRes.data || [])
        // Build categories from payees
        const cats = [{ id: 'all', label: 'All Bills', count: payeesRes.data?.length || 0 }]
        const catCounts = {}
        payeesRes.data?.forEach(p => {
          const cat = p.category?.toLowerCase().replace(' ', '-') || 'other'
          catCounts[cat] = (catCounts[cat] || 0) + 1
        })
        Object.entries(catCounts).forEach(([id, count]) => {
          cats.push({ id, label: id.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()), count })
        })
        setCategories(cats)
        setAutopayCount(payeesRes.data?.filter(p => p.autopay).length || 0)
      }

      if (paymentsRes.success) {
        setPayments(paymentsRes.data || [])
        // Calculate total paid this month
        const thisMonth = new Date().getMonth()
        const thisYear = new Date().getFullYear()
        const monthlyPaid = (paymentsRes.data || [])
          .filter(p => {
            const d = new Date(p.created_at || p.date)
            return d.getMonth() === thisMonth && d.getFullYear() === thisYear && p.status === 'completed'
          })
          .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
        setTotalPaid(monthlyPaid)
      }

      if (upcomingRes.success) {
        setUpcomingBills(upcomingRes.data || [])
      }

      if (accountsRes.success) {
        setAccounts(accountsRes.data || [])
        if (accountsRes.data?.length > 0) {
          setPaymentData(prev => ({ ...prev, accountId: accountsRes.data[0].id }))
        }
      }
    } catch (err) {
      console.error('Error fetching bill data:', err)
      setError(t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const showSuccess = (message) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  // Filter payees
  const filteredPayees = payees.filter(payee => {
    const matchesCategory = selectedCategory === 'all' ||
                           payee.category?.toLowerCase().replace(' ', '-') === selectedCategory
    const matchesSearch = payee.name?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const totalUpcoming = upcomingBills.reduce((sum, bill) => sum + parseFloat(bill.amount || 0), 0)

  // Add payee
  const handleAddPayee = async () => {
    if (!newPayee.name) {
      setError(t('errors.payeeNameRequired'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await BillsAPI.addPayee(newPayee)
      if (res.success) {
        setPayees(prev => [...prev, res.data])
        setShowAddPayee(false)
        setNewPayee({ name: '', category: '', account_number: '', routing_number: '', autopay: false })
        showSuccess(t('messages.payeeAdded'))
      } else {
        setError(res.message || t('errors.addPayeeFailed'))
      }
    } catch (err) {
      setError(err.message || t('errors.addPayeeFailed'))
    } finally {
      setSaving(false)
    }
  }

  // Pay bill
  const handlePayBill = async () => {
    if (!selectedPayee || !paymentData.accountId || !paymentData.amount) {
      setError(t('errors.fillRequired'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await BillsAPI.pay(
        selectedPayee.id,
        paymentData.accountId,
        parseFloat(paymentData.amount),
        paymentData.scheduledDate || null,
        paymentData.memo
      )
      if (res.success) {
        setShowPayModal(false)
        setSelectedPayee(null)
        setPaymentData({ accountId: accounts[0]?.id || '', amount: '', scheduledDate: '', memo: '' })
        showSuccess(t('messages.paymentSubmitted'))
        fetchData()
      } else {
        setError(res.message || t('errors.paymentFailed'))
      }
    } catch (err) {
      setError(err.message || t('errors.paymentFailed'))
    } finally {
      setSaving(false)
    }
  }

  // Delete payee
  const handleDeletePayee = async (id) => {
    if (!confirm(t('payees.deleteConfirm'))) return
    try {
      const res = await BillsAPI.deletePayee(id)
      if (res.success) {
        setPayees(prev => prev.filter(p => p.id !== id))
        showSuccess(t('messages.payeeDeleted'))
      } else {
        setError(res.message || t('errors.deletePayeeFailed'))
      }
    } catch (err) {
      setError(err.message || t('errors.deletePayeeFailed'))
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">{t('loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">{t('title')}</h1>
              <p className="text-sm text-slate-500 mt-0.5">{t('subtitle')}</p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" />
                {t('refresh')}
              </button>
              <button
                onClick={() => setShowAddPayee(true)}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                {t('addPayee')}
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2"
            >
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">{successMessage}</span>
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">&times;</button>
            </motion.div>
          )}
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        {/* Alert for Upcoming Bills */}
        {upcomingBills.length > 0 && (
          <div className="mb-8 p-5 bg-orange-50 border border-orange-200 rounded-xl">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Bell className="w-5 h-5 text-orange-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-orange-900 mb-1">
                  {t('alert.billsDue', { count: upcomingBills.length })}
                </h3>
                <p className="text-xs text-orange-700 mb-3">
                  {t('alert.totalDue', { amount: `$${totalUpcoming.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}` })}
                </p>
                <button className="text-xs font-medium text-orange-700 hover:text-orange-800">
                  {t('alert.reviewBills')} →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 bg-slate-100 rounded-lg p-1 mb-8">
          {['payees', 'upcoming', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-2.5 rounded-md transition-colors text-sm font-medium ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {t(`tabs.${tab}`)}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Payees Tab */}
            {activeTab === 'payees' && (
              <div>
                {/* Search and Filter */}
                <div className="mb-6 space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder={t('search.placeholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                    />
                  </div>

                  <div className="flex gap-2 overflow-x-auto">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setSelectedCategory(cat.id)}
                        className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors text-sm font-medium ${
                          selectedCategory === cat.id
                            ? 'bg-slate-900 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {cat.label} ({cat.count})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payees List */}
                <div className="space-y-3">
                  {filteredPayees.length === 0 ? (
                    <div className="text-center py-12">
                      <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('payees.noPayees')}</h3>
                      <p className="text-xs text-slate-500">{t('payees.noPayeesDesc')}</p>
                    </div>
                  ) : (
                    filteredPayees.map((payee, index) => {
                      const IconComponent = getCategoryIcon(payee.category)
                      const colorClass = getCategoryColor(payee.category)
                      return (
                        <motion.div
                          key={payee.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="p-5 bg-white border border-slate-200 rounded-xl hover:border-slate-900 transition-all"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4 flex-1">
                              <div className={`w-12 h-12 rounded-lg ${colorClass} flex items-center justify-center`}>
                                <IconComponent className="w-6 h-6" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="text-sm font-semibold text-slate-900">{payee.name}</h3>
                                  {payee.autopay && (
                                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded">
                                      {t('payees.autopay')}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mb-2">
                                  {payee.category || t('categories.other')} • Account {payee.account_number ? `****${payee.account_number.slice(-4)}` : t('payees.accountNA')}
                                </p>
                                {payee.next_due && (
                                  <div className="flex items-center gap-4 text-xs">
                                    <div className="flex items-center gap-1 text-slate-600">
                                      <Calendar className="w-3 h-3" />
                                      {t('payees.due', { date: new Date(payee.next_due).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' }) })}
                                    </div>
                                    {payee.amount && (
                                      <div className="font-semibold text-slate-900">
                                        ${parseFloat(payee.amount).toLocaleString(i18n.language)}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setSelectedPayee(payee)
                                  setPaymentData(prev => ({ ...prev, amount: payee.amount || '' }))
                                  setShowPayModal(true)
                                }}
                                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-medium"
                              >
                                {t('payees.pay')}
                              </button>
                              <button
                                onClick={() => handleDeletePayee(payee.id)}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors text-slate-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )
                    })
                  )}
                </div>
              </div>
            )}

            {/* Upcoming Tab */}
            {activeTab === 'upcoming' && (
              <div>
                <h2 className="text-sm font-semibold text-slate-900 mb-6">{t('upcoming.title')}</h2>
                <div className="space-y-3">
                  {upcomingBills.length === 0 ? (
                    <div className="text-center py-12">
                      <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('upcoming.noBills')}</h3>
                      <p className="text-xs text-slate-500">{t('upcoming.noBillsDesc')}</p>
                    </div>
                  ) : (
                    upcomingBills.map((bill, index) => {
                      const IconComponent = getCategoryIcon(bill.category)
                      const colorClass = getCategoryColor(bill.category)
                      const daysUntilDue = Math.ceil((new Date(bill.due_date || bill.next_due) - new Date()) / (1000 * 60 * 60 * 24))
                      return (
                        <motion.div
                          key={bill.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="p-5 bg-white border border-slate-200 rounded-xl"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 rounded-lg ${colorClass} flex items-center justify-center`}>
                                <IconComponent className="w-6 h-6" />
                              </div>
                              <div>
                                <h3 className="text-sm font-semibold text-slate-900 mb-1">{bill.payee_name || bill.name}</h3>
                                <p className="text-xs text-slate-500">
                                  {t('upcoming.dueIn', { count: daysUntilDue })}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-slate-900">${parseFloat(bill.amount || 0).toLocaleString(i18n.language)}</p>
                            </div>
                          </div>
                          {!bill.autopay ? (
                            <button
                              onClick={() => {
                                const payee = payees.find(p => p.id === bill.payee_id) || bill
                                setSelectedPayee(payee)
                                setPaymentData(prev => ({ ...prev, amount: bill.amount || '' }))
                                setShowPayModal(true)
                              }}
                              className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium"
                            >
                              {t('upcoming.payNow')}
                            </button>
                          ) : (
                            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg">
                              <Check className="w-4 h-4 text-emerald-600" />
                              <span className="text-xs font-medium text-emerald-700">
                                {t('upcoming.autopayScheduled', { date: new Date(bill.due_date || bill.next_due).toLocaleDateString(i18n.language) })}
                              </span>
                            </div>
                          )}
                        </motion.div>
                      )
                    })
                  )}
                </div>
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div>
                <h2 className="text-sm font-semibold text-slate-900 mb-6">{t('history.title')}</h2>
                <div className="space-y-3">
                  {payments.length === 0 ? (
                    <div className="text-center py-12">
                      <Receipt className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('history.noHistory')}</h3>
                      <p className="text-xs text-slate-500">{t('history.noHistoryDesc')}</p>
                    </div>
                  ) : (
                    payments.map((payment, index) => (
                      <motion.div
                        key={payment.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 bg-white border border-slate-200 rounded-lg"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-900 mb-1">{payment.payee_name || t('payModal.billPayment')}</p>
                            <p className="text-xs text-slate-500">{payment.is_autopay ? t('history.autopay') : t('history.manual')}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                            payment.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                            payment.status === 'pending' ? 'bg-yellow-50 text-yellow-600' :
                            payment.status === 'cancelled' ? 'bg-red-50 text-red-600' :
                            'bg-slate-50 text-slate-600'
                          }`}>
                            {t(`status.${payment.status || 'processing'}`)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-slate-500">
                            {new Date(payment.created_at || payment.date).toLocaleDateString(i18n.language, {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-sm font-bold text-slate-900">
                            ${parseFloat(payment.amount || 0).toLocaleString(i18n.language)}
                          </p>
                        </div>
                      </motion.div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Monthly Summary */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-4">{t('sidebar.thisMonth')}</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">{t('sidebar.totalPaid')}</p>
                  <p className="text-xl font-bold text-slate-900">${totalPaid.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">{t('sidebar.upcomingBills')}</p>
                  <p className="text-xl font-bold text-slate-900">
                    ${totalUpcoming.toLocaleString(i18n.language, { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">{t('sidebar.billsAutopay')}</p>
                  <p className="text-xl font-bold text-slate-900">
                    {autopayCount}
                  </p>
                </div>
              </div>
            </div>

            {/* AutoPay Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-blue-900 mb-1">{t('sidebar.autopayTitle')}</h3>
                  <p className="text-xs text-blue-700 leading-relaxed mb-3">
                    {t('sidebar.autopayDescription')}
                  </p>
                  <button className="text-xs font-medium text-blue-700 hover:text-blue-800">
                    {t('sidebar.learnMore')} →
                  </button>
                </div>
              </div>
            </div>

            {/* Security Note */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">{t('sidebar.securePayments')}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {t('sidebar.securityNote')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Payee Modal */}
      {showAddPayee && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">{t('addPayeeModal.title')}</h2>
              <button
                onClick={() => setShowAddPayee(false)}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">{t('addPayeeModal.payeeName')} *</label>
                <input
                  type="text"
                  value={newPayee.name}
                  onChange={(e) => setNewPayee(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('addPayeeModal.payeeNamePlaceholder')}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">{t('addPayeeModal.category')}</label>
                <select
                  value={newPayee.category}
                  onChange={(e) => setNewPayee(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                >
                  <option value="">{t('addPayeeModal.selectCategory')}</option>
                  <option value="Utilities">{t('categories.utilities')}</option>
                  <option value="Credit Card">{t('categories.creditCard')}</option>
                  <option value="Loan">{t('categories.loan')}</option>
                  <option value="Insurance">{t('categories.insurance')}</option>
                  <option value="Subscription">{t('categories.subscription')}</option>
                  <option value="Other">{t('categories.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">{t('addPayeeModal.accountNumber')}</label>
                <input
                  type="text"
                  value={newPayee.account_number}
                  onChange={(e) => setNewPayee(prev => ({ ...prev, account_number: e.target.value }))}
                  placeholder={t('addPayeeModal.accountNumberPlaceholder')}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="autopay"
                  checked={newPayee.autopay}
                  onChange={(e) => setNewPayee(prev => ({ ...prev, autopay: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <label htmlFor="autopay" className="text-sm text-slate-700">{t('addPayeeModal.enableAutopay')}</label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAddPayee(false)}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:border-slate-900"
              >
                {t('addPayeeModal.cancel')}
              </button>
              <button
                onClick={handleAddPayee}
                disabled={saving || !newPayee.name}
                className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('addPayeeModal.add')}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Pay Bill Modal */}
      {showPayModal && selectedPayee && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl max-w-md w-full p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-900">{t('payModal.title')}</h2>
              <button
                onClick={() => {
                  setShowPayModal(false)
                  setSelectedPayee(null)
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-4 bg-slate-50 rounded-lg mb-6">
              <p className="text-sm font-semibold text-slate-900">{selectedPayee.name}</p>
              <p className="text-xs text-slate-500">{selectedPayee.category || t('payModal.billPayment')}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">{t('payModal.payFrom')} *</label>
                <select
                  value={paymentData.accountId}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, accountId: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                >
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_name || acc.account_type} - ****{acc.account_number?.slice(-4)} (${parseFloat(acc.balance || 0).toLocaleString(i18n.language)})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">{t('payModal.amount')} *</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    className="w-full pl-8 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">{t('payModal.scheduleDate')}</label>
                <input
                  type="date"
                  value={paymentData.scheduledDate}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, scheduledDate: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-2">{t('payModal.memo')}</label>
                <input
                  type="text"
                  value={paymentData.memo}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, memo: e.target.value }))}
                  placeholder={t('payModal.memoPlaceholder')}
                  className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPayModal(false)
                  setSelectedPayee(null)
                }}
                className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:border-slate-900"
              >
                {t('payModal.cancel')}
              </button>
              <button
                onClick={handlePayBill}
                disabled={saving || !paymentData.amount || !paymentData.accountId}
                className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {paymentData.scheduledDate ? t('payModal.schedulePayment') : t('payModal.payNow')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
